import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/lib/auth";
import { AUDIENCES, FEATURES, HALLS, STATS } from "@/lib/content";

export default function Home() {
  const { isAuthed, user } = useAuth();
  const nav = useNavigate();
  const enter = () => nav(isAuthed && user ? homeFor(user) : "/login");

  return (
    <div>
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-14 pt-8 pb-16">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-end">
          <div>
            <span className="glass inline-flex items-center gap-1.5 rounded-full px-3 py-1">
              <Sparkles className="h-3 w-3 text-cobalt-500" />
              <span className="text-mono text-[10px] tracking-widest uppercase">Events platform · BMSIT&M</span>
            </span>
            <h1 className="mt-6 font-display font-black leading-[0.9] tracking-tighter text-[48px] sm:text-[68px] lg:text-[84px]">
              Every hall<br />
              <span className="italic font-light">on campus,</span><br />
              <span className="bg-clip-text text-transparent bg-[linear-gradient(120deg,#0047FF_0%,#22D3EE_60%,#BEF264_100%)]">
                one booking desk.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground leading-relaxed">
              Request an auditorium, get it approved by the hall incharge, then run the whole
              event — registration, QR check-in and certificates — from a single place.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button onClick={enter} size="lg"
                className="rounded-full font-medium h-12 px-6 bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100">
                {isAuthed ? "Go to your dashboard" : "Sign in"} <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Link to="/features">
                <Button variant="outline" size="lg" className="rounded-full font-medium h-12 px-6">See what it does</Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {STATS.map(([a, b]) => (
                <div key={b} className="glass rounded-2xl px-4 py-3">
                  <div className="metric text-2xl">{a}</div>
                  <div className="text-xs text-muted-foreground">{b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Audience cards */}
          <div className="grid gap-5">
            {AUDIENCES.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.button key={r.key} onClick={enter}
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }} className="group text-left">
                  <div className="glass rounded-3xl p-5 flex items-center gap-5 hover:-translate-y-1 transition-transform">
                    <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${r.accent} grid place-items-center text-white shadow-[0_10px_30px_rgba(0,71,255,0.35)]`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-2xl font-extrabold tracking-tighter">{r.title}</h3>
                        <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-sm text-muted-foreground">{r.tagline}</div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {r.perks.map((p) => (
                          <span key={p} className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-full bg-black/5 dark:bg-white/5">{p}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURES TEASER */}
      <section className="mx-auto max-w-7xl px-6 lg:px-14 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <div className="overline text-cobalt-500">Capabilities</div>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tighter">Everything an event needs, start to finish.</h2>
          </div>
          <Link to="/features" className="inline-flex items-center gap-1.5 text-sm font-medium text-cobalt-500 hover:underline whitespace-nowrap">
            See all features <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.slice(0, 3).map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="glass rounded-2xl p-6 card-hover">
                <div className="relative">
                  <div className="h-11 w-11 rounded-xl bg-cobalt-500/10 text-cobalt-500 grid place-items-center"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-4 font-display text-xl font-extrabold tracking-tighter">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HALLS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-14 py-16">
        <div className="glass-strong rounded-3xl p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-cyanx/30 blur-3xl opacity-50" />
          <div className="relative">
            <div className="overline text-cobalt-500 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />On the platform</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tighter">Seven venues, bookable today.</h2>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {HALLS.map((h) => (
                <span key={h.code} className="inline-flex items-center gap-2 text-sm bg-black/5 dark:bg-white/5 rounded-full px-3.5 py-1.5">
                  <span className="text-mono text-[10px] uppercase tracking-widest text-cobalt-500">{h.code}</span>
                  {h.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-14 pb-24">
        <div className="glass-strong rounded-3xl p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -left-20 h-60 w-60 rounded-full bg-cobalt-500/20 blur-3xl opacity-50" />
          <div className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-chartreuse/20 blur-3xl opacity-40" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">Ready to book your next event?</h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-xl mx-auto">
              Sign in with your @bmsit.in email, or reach out to Arventiq Labs about bringing the platform to your campus.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button onClick={enter} size="lg"
                className="rounded-full font-medium h-12 px-6 bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100">
                {isAuthed ? "Go to your dashboard" : "Sign in"} <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Link to="/contact"><Button variant="outline" size="lg" className="rounded-full font-medium h-12 px-6">Contact us</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
